import { computed, action } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller from '@ember/controller';
import { isNone } from '@ember/utils';
import { task, dropTask } from 'ember-concurrency';
import { queryEncode } from 'cio/helpers/query';

import { OPTION_EVERYTHING, OPTION_EXISTING, OPTION_NEW, OPTION_MATCH_EMAIL } from 'cio/components/import-csv/setup';

import ApiService from 'cio/services/api';
import EnvironmentService from 'cio/services/environment';
import constructExportURL, { ExportType } from 'cio/utils/imports/export-url';
import AnalyticsService from 'cio/services/analytics';
import TagModel from 'cio/models/tag';
import ApiErrorsService from 'cio/services/api-errors';
import { formatAudiencesError, TYPES } from 'cio/utils/ad-audiences';
import ImportModel, { IMPORT_TYPES } from 'cio/models/import';
import { IdentifierKey } from 'cio/utils/identifiers';
import DS from 'ember-data';
import RouterService from '@ember/routing/router-service';
import { tracked } from '@glimmer/tracking';
import { CampaignUpdateTypes } from 'cio/models/campaign';

enum UseImportOption {
  Broadcast = 'broadcast',
  Campaign = 'campaign',
  AdAudience = 'adAudience',
}

export default class IndexController extends Controller {
  @service api!: ApiService;
  @service environment!: EnvironmentService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;
  @service router!: RouterService;

  declare model: ImportModel;

  readonly identifierKey = IdentifierKey;

  isNewImport = false;
  useImportOption?: UseImportOption;
  @tracked newBroadcastName?: string;
  @tracked newCampaignName?: string;
  newCampaignTags: TagModel[] = [];
  newCampaignDescription?: string;
  isAddingAudience = false;

  @computed('model.{name,filename,sheet.spreadsheetId}')
  get importName() {
    if (this.model.name) {
      return this.model.name;
    }

    if (this.model.sheet) {
      return `Sheet ID ${this.model.sheet.spreadsheetId}`;
    }

    return this.model.filename;
  }

  @computed('model.{filename,sheet.spreadsheetName}')
  get filename() {
    if (this.model.sheet) {
      return this.model.sheet.spreadsheetName;
    }

    return this.model.filename;
  }

  @computed('model.segment.id')
  get encodedSegmentFilter() {
    let segmentId = this.segment?.id;

    if (segmentId == null) {
      return;
    }

    let serializedSegmentFilter = {
      and: [{ segment: { id: segmentId } }],
    };

    return queryEncode(serializedSegmentFilter);
  }

  @computed('model.validateMeta.warningRows')
  get warningsCount() {
    return this.model.validateMeta?.warningRows ?? 0;
  }

  @computed('segment', 'model.{type,identifier}', 'model.validateRules.{matchEmail,onlyExisting,excludeExisting}')
  get importType() {
    let rules = this.model.validateRules;

    if (isNone(rules)) {
      return;
    }

    let { onlyExisting, excludeExisting, matchEmail } = rules;
    let target = 'people';

    if (this.model.type === IMPORT_TYPES.object) {
      target = this.model.objectType?.name ?? 'objects';
    }

    if (this.model.type === IMPORT_TYPES.relationship) {
      target = 'relationships';
    }

    if (this.segment && this.model.type === IMPORT_TYPES.segment) {
      return {
        label: `By ${this.model.identifier}: Update to Manual Segment`,
        value: matchEmail ? OPTION_MATCH_EMAIL : OPTION_EXISTING,
      };
    }

    if (onlyExisting) {
      return {
        label: `By ${this.model.identifier}: updates to existing ${target}`,
        value: OPTION_EXISTING,
      };
    } else if (excludeExisting) {
      return {
        label: `By ${this.model.identifier}: new ${target} only`,
        value: OPTION_NEW,
      };
    }

    return {
      label: `By ${this.model.identifier}: new and existing ${target}`,
      value: OPTION_EVERYTHING,
    };
  }

  @computed('model.segment.id')
  get segment() {
    if (this.model.segment) {
      return this.model.belongsTo('segment').value();
    }

    return null;
  }

  @computed('model.{rowsToImport,rowsImported}')
  get isComplete() {
    return this.model.rowsToImport === this.model.rowsImported;
  }

  @computed('model.{type,segment}')
  get summaryLinkDetails() {
    if (this.model.type === IMPORT_TYPES.event) {
      return { link: 'logs', description: 'View activity logs' };
    }

    if (this.model.type === IMPORT_TYPES.object) {
      return {
        link: 'objects.type.list',
        model: this.model.objectType?.slug ?? this.model.objectTypeId,
        description: `View ${this.model.objectType?.name ?? 'objects'}`,
      };
    }

    return {
      link: 'people.list',
      description: this.model.segment ? 'View people' : 'View full people list',
    };
  }

  @action
  selectUseImportOption(type: UseImportOption) {
    this.set('useImportOption', type);
  }

  @action
  deselectUseImportOption() {
    this.set('useImportOption', undefined);
  }

  @action
  addCampaignTag(tag: TagModel) {
    this.set('newCampaignTags', [...this.newCampaignTags, tag]);
  }

  @action
  removeCampaignTag(tag: TagModel) {
    let index = this.newCampaignTags.indexOf(tag);

    if (index >= 0) {
      this.set(
        'newCampaignTags',
        this.newCampaignTags.filter((tag) => tag !== tag),
      );
    }
  }

  @action
  stopAddingAudience() {
    // This is called when the close the modal AND when they click the finish button
    // so let's use the last task run to determine if they actually added it or not
    let wasCreated = this.createAdAudience.last?.isSuccessful;

    this.createAdAudience.cancelAll({ resetState: true });
    this.set('isAddingAudience', false);

    if (wasCreated && this.segment != null) {
      this.router.transitionTo('segments.segment.audiences', this.segment.id);
    } else {
      this.deselectUseImportOption();
    }
  }

  importUrl = task(async () => {
    let importUrlProxy = constructExportURL(ExportType.Original, this.model);

    if (!importUrlProxy || !this.model.isOriginalAvailable) {
      return;
    }

    let { url } = await this.api.post(importUrlProxy);

    return url;
  });

  // Due to the way newsletters are created and updated on the back end, we need to first create
  // the base model, then update it with the segment filters
  createAndVisitNewsletter = dropTask(async () => {
    try {
      let segments = this.segment != null ? [parseInt(this.segment.id)] : [];
      let filters = [{ type: 'segments_negatable', segments, negate: false }];
      let newsletter = this.store.createRecord('newsletter', { name: this.newBroadcastName });

      await newsletter.save();

      newsletter.setProperties({
        filters,
        updateType: 'recipients',
      });

      await newsletter.save();

      this.analytics.track('broadcast', 'create_started', { source: 'Import Detail' });
      this.router.transitionTo('broadcasts.newsletter', newsletter.id);
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  // Due to the way campaigns are created and updated on the back end, we need to first create
  // the base model, then update it with the segment filters
  createAndVisitCampaign = dropTask(async () => {
    try {
      let segments = this.segment != null ? [parseInt(this.segment.id)] : [];
      let filters = [{ type: 'segments_negatable', segments, negate: false }];
      let campaign = this.store.createRecord('campaign', {
        name: this.newCampaignName,
        description: this.newCampaignDescription,
        tags: this.newCampaignTags,
        type: 'behavioral',
      });

      await campaign.save();

      campaign.setProperties({
        anchors: filters,
        updateType: CampaignUpdateTypes.Recipients,
      });

      await campaign.save();

      this.analytics.track('campaign', 'create_started', { source: 'Import Detail' });
      this.router.transitionTo('campaigns.campaign', campaign.id);
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  createAdAudience = dropTask(async (attrs: Record<string, any>) => {
    let audience = this.store.createRecord('audience', attrs);

    try {
      await audience.save();
      return audience;
    } catch (res) {
      audience.rollbackAttributes();

      let {
        errors: [error],
      } = res;

      let message;

      if (Number(error.status) === 422) {
        if (attrs.type === TYPES.facebook) {
          message = error.meta?.user_message ?? error.detail;
        } else if (attrs.type === TYPES.adwords) {
          message = error.meta?.message ?? error.detail;
        }
      } else {
        message = 'Something went wrong. Try again.';
      }

      throw new Error(formatAudiencesError(message));
    }
  });
}
